<script lang="ts">
  import Icon from '../icon/icon.svelte';
  import type { HistoryItem } from '../models/history.type';
  import Hover from './hover.svelte';
  import Scrollable from './scrollable.svelte';
  import Text from './text.svelte';
  import { flip } from 'svelte/animate';
  import { cubicOut } from 'svelte/easing';

  interface Props {
    history?: HistoryItem[];
  }

  let { history = [] }: Props = $props();

  // Not 'technique' or 'ingredients' for class names: game-board.less styles
  // both globally.
  type Kind = 'swap' | 'end' | 'move';

  function kindOf(item: HistoryItem): Kind {
    if (item.ingredients?.[0] == 'endturn') return 'swap';
    if (item.ingredients?.[0] == 'grave') return 'end';
    return 'move';
  }

  function actor(item: HistoryItem) {
    return item.player?.title || (item.player?.id == 'player' ? 'You' : 'The enemy');
  }

  // Log lines are written in a couple of dozen places and disagree on
  // capitals and full stops, so even them out for display.
  function tidy(line: string) {
    const trimmed = line.trim();
    if (!trimmed) return trimmed;
    const capitalised = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    return /[.!?]$/.test(capitalised) ? capitalised : capitalised + '.';
  }

  type Effect = 'damage' | 'heal' | 'gain' | 'loss' | 'other';

  // What a log line did, read from its wording, so it can be coloured.
  function effectOf(line: string): Effect {
    if (/damage/i.test(line)) return 'damage';
    if (/heal/i.test(line)) return 'heal';
    if (/^\s*removed/i.test(line)) return 'loss';
    if (/^\s*(created|added|gained)/i.test(line)) return 'gain';
    return 'other';
  }

  // The most notable thing a turn change did, for its marker on the bar.
  function headline(item: HistoryItem): Effect | null {
    const effects = (item.actions ?? []).map(effectOf);
    if (!effects.length) return null;
    for (const effect of ['damage', 'heal'] as Effect[]) {
      if (effects.includes(effect)) return effect;
    }
    return 'other';
  }

  // Quick, because the enemy's turn adds entries in bursts. Skipped entirely
  // for anyone who has asked their system for less motion.
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const duration = reduceMotion ? 0 : 250;

  // A new entry grows in from nothing at the front of the bar, and an undone
  // one shrinks back out. flip slides the rest along to make or close the gap.
  function arrive(_node: Element) {
    return {
      duration,
      easing: cubicOut,
      css: (t: number) => `opacity: ${t}; transform: scale(${0.4 + 0.6 * t});`,
    };
  }
</script>

<Scrollable>
  <div class="history-container">
    <div class="history">
      {#each history as item, index (item)}
        {@const kind = kindOf(item)}
        <div
          class="item {kind} {item.player?.id ?? ''}"
          class:latest={index === 0}
          in:arrive
          out:arrive
          animate:flip={{ duration }}
        >
          {#if kind == 'move'}
            <div class="backdrop {item.player?.archetype || item.player?.type}"></div>
            <div class="glyphs">
              {#each item.ingredients ?? [] as icon}
                <Icon name={icon} />
              {/each}
            </div>
          {:else}
            <Icon name={item.ingredients[0]} />
            {#if kind == 'swap' && headline(item)}
              <span class="marker {headline(item)}" aria-hidden="true"></span>
            {/if}
          {/if}

          <Hover touch="tap">
            <div class="tip">
              {#if kind == 'swap'}
                <p class="eyebrow">Turn change</p>
                <h2>{actor(item)}'s turn</h2>
              {:else if kind == 'end'}
                <h2>{item.title}</h2>
              {:else}
                <p class="eyebrow">{actor(item)} used</p>
                <h2>{item.title}</h2>
                {#if item.ingredients?.length}
                  <div class="tip-ingredients">
                    {#each item.ingredients as icon}
                      <Icon name={icon} />
                    {/each}
                  </div>
                {/if}
                {#if item.text}
                  <Text class="summary" data={item.text} interactive={false} />
                {/if}
              {/if}

              {#if item.actions?.length}
                <p class="section-label">{kind == 'swap' ? 'Start of turn' : 'What happened'}</p>
                <ul>
                  {#each item.actions as line}
                    <li class={effectOf(line)}><Text data={tidy(line)} interactive={false} /></li>
                  {/each}
                </ul>
              {/if}
            </div>
          </Hover>
        </div>
      {/each}
    </div>
  </div>
</Scrollable>

<style lang="less">
  @import '../../defines.less';

  :global(.history-container) {
    --item-size: 90px;
    --icon-size: 30px;
  }

  @media @medium {
    :global(.history-container) {
      --item-size: 70px;
      --icon-size: 25px;
    }
  }

  @media @small {
    :global(.history-container) {
      --item-size: 60px;
      --icon-size: 20px;
    }
  }

  .history-container {
    width: 100%;
    padding: 6px;
    box-sizing: border-box;

    &::-webkit-scrollbar {
      width: 0px;
      height: 0px;
    }

    .history {
      display: flex;
      flex-direction: row;
      grid-gap: 10px;
      align-items: center;
      width: fit-content;
    }
  }

  .item {
    // Whose entry it is, in the same colours as the two portraits.
    --tone: @enemy-tone;

    position: relative;
    flex: 0 0 auto;
    width: var(--item-size);
    height: var(--item-size);
    display: grid;
    place-items: center;
    box-sizing: border-box;
    border-radius: 6px;
    border: 2px solid var(--tone);
    background: @panel-raised;
    box-shadow: inset 0 0 12px fade(@black, 60%);

    &.player {
      --tone: var(--player-colour);
    }

    // Who acted, as their portrait held well back so the icons on top stay
    // readable: faded, drained of most colour, and under the scrim below.
    .backdrop {
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background-size: cover;
      background-position: top center;
      opacity: 0.55;
      filter: grayscale(0.35);
      pointer-events: none;
    }

    // A wash of the owner's colour, and a scrim darkening towards the bottom.
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background:
        linear-gradient(to bottom, fade(@black, 0%), fade(@black, 45%)),
        color-mix(in srgb, var(--tone) 16%, transparent);
      pointer-events: none;
    }

    .glyphs {
      position: relative;
      z-index: 1;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      grid-gap: 2px;
      max-width: 90%;
    }

    // Turn changes divide the techniques rather than compete with them.
    &.swap {
      --icon-size: calc(var(--item-size) * 0.22);

      width: calc(var(--item-size) * 0.34);
      border-style: dashed;
      background: transparent;
      box-shadow: none;

      // Dimmed on the icon, never the tile: opacity on the tile makes a
      // stacking context that traps its tip under the technique list.
      :global(.icon) {
        position: relative;
        z-index: 1;
        opacity: 0.75;
      }

      // Just the colour wash; there is no portrait here to hold back.
      &::after {
        background: color-mix(in srgb, var(--tone) 16%, transparent);
      }
    }

    // A turn change that did something -- damage from bleed, say -- says so
    // on the bar instead of looking like every other divider.
    .marker {
      position: absolute;
      z-index: 1;
      bottom: 4px;
      left: 50%;
      width: 8px;
      height: 8px;
      margin-left: -4px;
      border-radius: 50%;
      background: @edge-shine;
      box-shadow: 0 0 4px @black;

      &.damage {
        background: @red;
        box-shadow: 0 0 6px @red;
      }
      &.heal {
        background: @green;
        box-shadow: 0 0 6px @green;
      }
    }

    &.end {
      border-style: double;
      border-width: 4px;
    }

    &.latest {
      box-shadow:
        inset 0 0 12px fade(@black, 60%),
        0 0 0 2px fade(@edge-shine, 45%),
        0 0 10px fade(@edge-shine, 35%);
      // A brief flare as it lands, settling into the steady glow above.
      animation: landed 700ms ease-out;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }
  }

  .tip {
    min-width: 220px;
    max-width: min(420px, 80vw);
    text-align: left;
    line-height: 1.4;

    .eyebrow {
      margin: 0;
      font-size: 0.75em;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      // Lifted towards white: several class colours are too dark to read as
      // small text on the tip.
      color: color-mix(in srgb, var(--tone) 60%, white);
    }

    h2 {
      margin: 2px 0 8px;
      line-height: 1.15;
    }

    .tip-ingredients {
      --icon-size: 22px;

      display: flex;
      flex-wrap: wrap;
      grid-gap: 4px;
      margin-bottom: 8px;
    }

    .section-label {
      margin: 12px 0 4px;
      padding-top: 8px;
      border-top: 1px solid @panel-line;
      font-size: 0.7em;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      opacity: 0.6;
    }

    ul {
      margin: 0;
      padding: 0;
      list-style: none;
    }

    // Each effect is ruled in the colour of what it did.
    li {
      margin: 4px 0;
      padding: 2px 0 2px 10px;
      border-left: 2px solid var(--tone);

      &.damage {
        border-left-color: @red;
        background: linear-gradient(to right, fade(@red, 22%), transparent 70%);
      }
      &.heal {
        border-left-color: @green;
        background: linear-gradient(to right, fade(@green, 22%), transparent 70%);
      }
      &.loss {
        border-left-color: @gray;
        opacity: 0.8;
      }
    }
  }

  @keyframes landed {
    from {
      box-shadow:
        inset 0 0 12px fade(@black, 60%),
        0 0 0 3px fade(@edge-shine, 90%),
        0 0 22px fade(@edge-shine, 80%);
    }
  }
</style>
